/**
 * "Browse light": lists show a finished drawing / vignette figure as a build-time WebP (scripts/thumbs.tsx) instead of
 * a live SVG figure (hundreds of SVG nodes + masks + filters each). The vector figure is still used when:
 *   - the thumbnail manifest has no entry (runtime remixes, legacy stroke drawings),
 *   - the box is larger than the biggest thumbnail can cover sharply (big views),
 *   - the image fails to load.
 * Manifests are small JSON files fetched once per art style (style A ships inside the native apps; B / C load from
 * the web origin on native).
 */
import { useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { useInView } from '../lib/inView';
import { isNative } from '../lib/platform';
import { imageDpr, pickWidth } from '../lib/responsiveImage';
import { THUMB_WIDTHS } from './thumbKeys';
import type { StyleId } from './tokens';

type Entry = [hash: string, lqip: string, aspect: number, sig: string];
type Manifest = Record<string, Entry>;

const WEB_ORIGIN = (import.meta.env.VITE_WEB_ORIGIN as string | undefined) ?? 'https://sketchling-web.pages.dev';
/** Thumbnails are off in unit tests / SSR and with `?thumbs=0` (A/B comparisons). */
const ENABLED = typeof window !== 'undefined' && !/[?&]thumbs=0\b/.test(window.location.search);
export const thumbBase = (style: StyleId) => (isNative && style !== 'A' ? `${WEB_ORIGIN}/thumbs/${style}` : `/thumbs/${style}`);

const manifests = new Map<StyleId, Manifest | null>();
const listeners = new Set<() => void>();
function load(style: StyleId) {
  if (manifests.has(style)) return;
  manifests.set(style, null);
  const base = thumbBase(style).replace(/\/[ABC]$/, '');
  fetch(`${base}/manifest-${style}.json`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((m: Manifest) => {
      manifests.set(style, m);
      listeners.forEach((l) => l());
    });
}
const subscribe = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));

/** The manifest of a style: undefined while loading, {} when unavailable. */
export function useThumbManifest(style: StyleId): Manifest | undefined {
  if (ENABLED) load(style);
  return useSyncExternalStore(subscribe, () => (ENABLED ? (manifests.get(style) ?? undefined) : EMPTY));
}
const EMPTY: Manifest = {};
/** Start loading the active style's manifest early (app start). */
export const preloadThumbs = (style: StyleId) => ENABLED && load(style);

/** Largest box (CSS px × dpr) a thumbnail may fill before the vector figure looks sharper. */
const MAX_PX = 480 * 1.2;

export function ArtThumb({ thumbKey, sig, style, className = '', paper, alt, imgStyle, vector }: { thumbKey: string; sig: string | undefined; style: StyleId; className?: string; paper?: string; alt?: string; imgStyle?: CSSProperties; vector: () => ReactNode }) {
  const man = useThumbManifest(style);
  const hit = man?.[thumbKey];
  // sig undefined = a light (not yet hydrated) drawing: trust the build's thumbnail (same build as the content shell).
  const entry = hit && (sig === undefined || hit[3] === sig) ? hit : undefined;
  const ref = useRef<HTMLImageElement | null>(null);
  // Decoded only while near the screen; released (src removed) when scrolled away.
  const [ioRef, inView] = useInView<HTMLImageElement>();
  const setRef = useCallback(
    (n: HTMLImageElement | null) => {
      ref.current = n;
      ioRef(n);
    },
    [ioRef],
  );
  const [w, setW] = useState(0);
  const [vectorMode, setVector] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || w) return;
    const cw = Math.max(el.clientWidth, el.clientHeight * (entry?.[2] ?? 1));
    if (cw * imageDpr() > MAX_PX) setVector(true);
    else setW(pickWidth(THUMB_WIDTHS, cw || 160));
  }, [entry, w]);
  if (man === undefined) return <span className={`thumb thumb-ph ${className}`} aria-hidden style={{ background: paper }} />;
  if (!entry || vectorMode) return <>{vector()}</>;
  const [hash, lqip, aspect] = entry;
  return (
    <img
      ref={setRef}
      className={`thumb ${className}`}
      src={w && inView ? `${thumbBase(style)}/${hash}-${w}.webp` : undefined}
      alt={alt ?? ''}
      decoding="async"
      loading="lazy"
      draggable={false}
      style={{ aspectRatio: String(aspect), backgroundColor: paper, backgroundImage: loaded && inView ? undefined : `url(${lqip})`, ...imgStyle }}
      onLoad={() => setLoaded(true)}
      onError={() => setVector(true)}
    />
  );
}
